import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PetCard from '@/components/PetCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, MapPin, Calendar, User, Mail, X, ExternalLink } from 'lucide-react';

const GalleryPage = () => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    porte: '',
    especie: '',
    search: ''
  });
  const [selectedPet, setSelectedPet] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchPets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.porte) params.append('porte', filters.porte);
      if (filters.especie) params.append('especie', filters.especie);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/pets?${params.toString()}`);
      setPets(response.data);
    } catch (error) {
      console.error('Error fetching pets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPets();
  }, [filters]);

  const clearFilters = () => {
    setFilters({ status: '', porte: '', especie: '', search: '' });
  };

  const hasActiveFilters = filters.status || filters.porte || filters.especie || filters.search;

  const statusColors = {
    perdido: 'bg-[#E57373]',
    encontrado: 'bg-[#81C784]',
    adocao: 'bg-[#64B5F6]'
  };

  const statusLabels = {
    perdido: 'Perdido',
    encontrado: 'Encontrado',
    adocao: 'Para Adoção'
  };

  const porteLabels = {
    pequeno: 'Pequeno',
    medio: 'Médio',
    grande: 'Grande'
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#F5D79C]">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-display font-bold text-[#5D4A3A] mb-4">Galeria de Pets</h1>
            <p className="text-[#7A5B3E] max-w-2xl mx-auto">
              Encontre pets perdidos, achados ou disponíveis para adoção. Use os filtros para refinar sua busca.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-[#FCEBC5] rounded-2xl shadow-lg p-6 mb-8 border border-[#C4956A]">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A5B3E]" />
                <Input
                  placeholder="Buscar por nome, espécie ou descrição..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="input-original pl-12"
                  data-testid="search-input"
                />
              </div>

              {/* Filter Toggle (Mobile) */}
              <Button
                variant="outline"
                className="md:hidden rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
              </Button>

              {/* Desktop Filters */}
              <div className="hidden md:flex gap-3">
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-40 rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FCEBC5] border-[#C4956A]">
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="perdido">Perdido</SelectItem>
                    <SelectItem value="encontrado">Encontrado</SelectItem>
                    <SelectItem value="adocao">Adoção</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.porte}
                  onValueChange={(value) => setFilters({ ...filters, porte: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-40 rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]" data-testid="filter-porte">
                    <SelectValue placeholder="Porte" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FCEBC5] border-[#C4956A]">
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pequeno">Pequeno</SelectItem>
                    <SelectItem value="medio">Médio</SelectItem>
                    <SelectItem value="grande">Grande</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    className="text-[#6B5344] hover:bg-[#F5E6C8] rounded-full"
                    onClick={clearFilters}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="md:hidden mt-4 pt-4 border-t border-[#C4956A] space-y-3">
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-full rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FCEBC5] border-[#C4956A]">
                    <SelectItem value="all">Todos os Status</SelectItem>
                    <SelectItem value="perdido">Perdido</SelectItem>
                    <SelectItem value="encontrado">Encontrado</SelectItem>
                    <SelectItem value="adocao">Adoção</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.porte}
                  onValueChange={(value) => setFilters({ ...filters, porte: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-full rounded-full border-[#C4956A] bg-[#F5E6C8] text-[#5D4A3A]">
                    <SelectValue placeholder="Porte" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FCEBC5] border-[#C4956A]">
                    <SelectItem value="all">Todos os Portes</SelectItem>
                    <SelectItem value="pequeno">Pequeno</SelectItem>
                    <SelectItem value="medio">Médio</SelectItem>
                    <SelectItem value="grande">Grande</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    className="w-full text-[#6B5344] border-[#C4956A] rounded-full"
                    onClick={clearFilters}
                  >
                    Limpar Filtros
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-[#7A5B3E]">
              {loading ? 'Carregando...' : `${pets.length} pet${pets.length !== 1 ? 's' : ''} encontrado${pets.length !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Pet Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="bg-[#FCEBC5] rounded-2xl h-80 animate-pulse border border-[#C4956A]"></div>
              ))}
            </div>
          ) : pets.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {pets.map(pet => (
                <PetCard key={pet.id} pet={pet} onClick={setSelectedPet} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#FCEBC5] rounded-2xl border border-[#C4956A]">
              <span className="text-6xl">🐾</span>
              <h3 className="text-xl font-bold text-[#5D4A3A] mt-4 mb-2">Nenhum pet encontrado</h3>
              <p className="text-[#7A5B3E] mb-4">Tente ajustar os filtros ou faça uma nova busca</p>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  className="rounded-full border-[#C4956A] text-[#6B5344]"
                  onClick={clearFilters}
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Pet Detail Modal */}
      <Dialog open={!!selectedPet} onOpenChange={() => setSelectedPet(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FCEBC5] border-[#C4956A]" aria-describedby="pet-detail-description">
          {selectedPet && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-display font-bold text-[#5D4A3A] flex items-center gap-3">
                  {selectedPet.nome}
                  <Badge className={`${statusColors[selectedPet.status]} text-white rounded-full`}>
                    {statusLabels[selectedPet.status]}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Image */}
                <div className="rounded-xl overflow-hidden border border-[#C4956A]">
                  {selectedPet.imagem_url ? (
                    <img 
                      src={selectedPet.imagem_url} 
                      alt={selectedPet.nome}
                      className="w-full h-64 object-cover"
                    />
                  ) : (
                    <div className="w-full h-64 bg-[#F5E6C8] flex items-center justify-center">
                      <span className="text-8xl">🐾</span>
                    </div>
                  )}
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#F5D79C] rounded-xl p-4 border border-[#C4956A]">
                    <p className="text-sm text-[#7A5B3E]">Espécie</p>
                    <p className="font-semibold text-[#5D4A3A]">{selectedPet.especie}</p>
                  </div>
                  <div className="bg-[#F5D79C] rounded-xl p-4 border border-[#C4956A]">
                    <p className="text-sm text-[#7A5B3E]">Raça</p>
                    <p className="font-semibold text-[#5D4A3A]">{selectedPet.raca || 'Não informada'}</p>
                  </div>
                  <div className="bg-[#F5D79C] rounded-xl p-4 border border-[#C4956A]">
                    <p className="text-sm text-[#7A5B3E]">Porte</p>
                    <p className="font-semibold text-[#5D4A3A]">{porteLabels[selectedPet.porte]}</p>
                  </div>
                  <div className="bg-[#F5D79C] rounded-xl p-4 border border-[#C4956A]">
                    <p className="text-sm text-[#7A5B3E]">Cadastrado em</p>
                    <p className="font-semibold text-[#5D4A3A] text-sm">{formatDate(selectedPet.created_at)}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="font-semibold text-[#5D4A3A] mb-2">Descrição</h4>
                  <p id="pet-detail-description" className="text-[#7A5B3E]">{selectedPet.descricao}</p>
                </div>

                {/* Location with Google Maps Link */}
                <div className="bg-[#F5D79C] rounded-xl p-4 border border-[#C4956A]">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#6B5344] mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-[#7A5B3E]">Localização</p>
                      {selectedPet.ponto_referencia && (
                        <p className="font-medium text-[#5D4A3A] mb-2">{selectedPet.ponto_referencia}</p>
                      )}
                      <a 
                        href={`https://www.google.com/maps?q=${selectedPet.latitude},${selectedPet.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#6B5344] text-white px-4 py-2 rounded-full text-sm hover:bg-[#5D4A3A] transition-colors"
                      >
                        <MapPin className="w-4 h-4" />
                        Ver no Google Maps
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="border-t border-[#C4956A] pt-4">
                  <h4 className="font-semibold text-[#5D4A3A] mb-3">Contato</h4>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#6B5344] rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-[#5D4A3A]">{selectedPet.usuario_nome}</p>
                      <a 
                        href={`mailto:miaucaomigo@gmail.com?subject=Contato sobre o pet ${selectedPet.nome} (${statusLabels[selectedPet.status]})&body=Olá! Vi o anúncio do pet "${selectedPet.nome}" cadastrado por ${selectedPet.usuario_nome} no MiauCãoMigo e gostaria de mais informações.`}
                        className="inline-flex items-center gap-2 bg-[#6B5344] text-white px-4 py-2 rounded-full text-sm hover:bg-[#5D4A3A] transition-colors mt-2"
                        data-testid="modal-contact-email"
                      >
                        <Mail className="w-4 h-4" />
                        Entrar em Contato
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default GalleryPage;
